<template>
  <div id="app">
    <!-- Skip to main content link for keyboard users -->
    <a
      href="#main-content"
      class="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-skip-link focus:bg-white focus:text-indigo-600 focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg"
      @click.prevent="skipToMain"
    >
      {{ $t('app.skip_to_main') }}
    </a>

    <!-- Live region for announcements -->
    <div
      id="a11y-announcer"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      class="sr-only"
    >
      {{ announcement }}
    </div>

    <!-- Welcome Screen for Unauthenticated Users -->
    <div
      v-if="isGuest && !isAuthLoading"
      class="min-h-screen w-full flex items-center justify-center fixed inset-0 z-header"
      style="background-color: var(--bg-primary);"
      role="main"
      :aria-label="$t('app.welcome_screen')"
    >
      <div class="w-full max-w-[340px] px-6">
        <!-- The seam, at full brand scale. --pot resolves to the shared pot
             here: the watcher in this file pins it while signed out. -->
        <div class="mb-8" style="border-top: 4px solid var(--pot-mark); width: 72px;"></div>

        <!-- App Name -->
        <h1 class="wordmark text-4xl mb-3 text-primary">
          <span class="wordmark__shared">felles</span><span class="wordmark__pot">potten</span>
        </h1>

        <p class="label-caps mb-12">{{ $t('app.tagline') }}</p>

        <!-- Auth Buttons -->
        <div class="flex flex-col gap-3">
          <AppButton block @click="showAuthModal = true; authModalMode = 'login'">
            {{ $t('auth.sign_in') }}
          </AppButton>
          <!-- Only offered when email sign-up is enabled — otherwise the modal
               would open locked to login and the button would dead-end. -->
          <AppButton v-if="REGISTRATION_ENABLED" variant="outline" block @click="showAuthModal = true; authModalMode = 'register'">
            {{ $t('auth.create_account') }}
          </AppButton>
          <!-- Gated on the same flag as Create Account: an anonymous sign-in IS
               account creation, so Identity Platform's disabledUserSignup
               rejects it with ADMIN_ONLY_OPERATION exactly like a register
               attempt. Offering it with sign-ups off is the same dead end. -->
          <AppButton v-if="REGISTRATION_ENABLED" variant="ghost" block :loading="guestLoading" @click="handleGuestSignIn">
            {{ $t('auth.continue_as_guest') }}
          </AppButton>
          <p v-if="guestError" class="m-0" role="alert" style="color: var(--ruby-700); font-size: var(--text-xs);">
            {{ guestError }}
          </p>
        </div>
      </div>
    </div>

    <!-- Authenticated App -->
    <template v-else-if="!isAuthLoading">
      <!-- Mobile Header - only show on small screens. No hamburger: the
           drawer is reachable via the bottom bar's "More" item, so the top
           bar keeps its width for the title and actions. -->
      <header class="mobile-header" role="banner">
        <h1>
          <button
            type="button"
            class="header-title-btn"
            @click="openGlobalSearch"
            :aria-label="$t('common.search')"
          >
            <span class="wordmark__shared">felles</span><span class="wordmark__pot">potten</span>
          </button>
        </h1>
        <div class="header-actions">
          <button
            type="button"
            class="header-icon-btn"
            @click="openGlobalSearch"
            :aria-label="$t('common.search')"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </button>
          <ThemeToggle />
        </div>
      </header>

      <!-- Sidebar Overlay - only show on small screens when sidebar is open -->
      <div
        v-if="sidebarOpen"
        class="sidebar-overlay active"
        @click="sidebarOpen = false"
        @keydown.escape="sidebarOpen = false"
        aria-hidden="true"
      ></div>

      <!-- Sidebar - hidden on mobile by default, always visible on large screens -->
      <nav
        id="main-navigation"
        class="sidebar"
        :class="{ 'sidebar-open': sidebarOpen }"
        role="navigation"
        :aria-label="$t('a11y.main_navigation')"
      >
        <div class="sidebar-header">
          <!-- The generic currency glyph is gone: the seam already carries the
               brand colour here, and the wordmark carries the name. -->
          <div class="sidebar-brand">
            <div class="brand-text">
              <h2 class="wordmark">
                <span class="wordmark__shared">felles</span><span class="wordmark__pot">potten</span>
              </h2>
              <p>{{ $t('app.tagline') }}</p>
            </div>
          </div>
        </div>

        <!-- Sidebar Search Trigger (opens the global command palette) -->
        <div class="sidebar-search">
          <button
            type="button"
            class="sidebar-search-btn"
            @click="openGlobalSearch"
            :aria-label="$t('search.global.title')"
          >
            <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
            <span class="sidebar-search-text">{{ $t('search.global.trigger') }}</span>
            <span class="sidebar-search-kbd" aria-hidden="true">
              <kbd>⌘</kbd><kbd>K</kbd>
            </span>
          </button>
        </div>

        <!-- Budget Context Switcher (only for authenticated non-anonymous users) -->
        <div v-if="currentUser && !currentUser.isAnonymous" class="sidebar-context-switcher">
          <BudgetContextSwitcher @navigate="selectTab" />
        </div>

        <div class="sidebar-nav" role="tablist" :aria-label="$t('a11y.main_sections')">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            class="sidebar-item"
            :class="{ 'active': activeTab === tab.id }"
            @click="selectTab(tab.id)"
            role="tab"
            :id="`tab-${tab.id}`"
            :aria-selected="activeTab === tab.id"
            :aria-controls="`panel-${tab.id}`"
            :tabindex="activeTab === tab.id ? 0 : -1"
            @keydown="handleTabKeydown($event, tab.id)"
          >
            <component :is="tab.icon" class="sidebar-icon" aria-hidden="true" />
            <span>{{ $t(`navigation.${tab.id}`) }}</span>
          </button>
        </div>

        <div class="sidebar-footer">
          <div v-if="currentUser" class="user-section">
            <button
              @click="showUserProfile = true"
              class="user-profile-btn"
              :aria-label="$t('app.open_profile')"
              aria-haspopup="dialog"
            >
              <AppAvatar size="sm" :image-id="firestoreState.preferences.value?.avatarImageId" :src="currentUser.photoURL" :name="currentUser.displayName || 'U'" :alt="currentUser.displayName || $t('app.user_fallback')" />
              <div class="user-info">
                <p class="user-name">{{ currentUser.displayName || $t('app.user_fallback') }}</p>
                <p class="user-email">{{ currentUser.email || (currentUser.isAnonymous ? $t('app.guest_user') : $t('app.user_fallback')) }}</p>
              </div>
              <svg class="chevron-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </button>
            <div class="sidebar-actions">
              <ThemeToggle />
              <LanguageSwitcher />
            </div>
          </div>
          <div v-else>
            <AppButton block @click="showAuthModal = true">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m0 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
              </svg>
              {{ $t('auth.sign_in') }}
            </AppButton>
          </div>
        </div>
      </nav>

      <!-- Main Content -->
      <main
        id="main-content"
        class="main-content"
        style="background: var(--bg-primary);"
        role="main"
        :aria-label="$t('a11y.section', { name: $t(`navigation.${activeTab}`) })"
      >
        <PullToRefresh class="content-wrapper" :on-refresh="handlePullToRefresh">
          <div
            v-for="tab in allTabs"
            :key="tab.id"
            :id="`panel-${tab.id}`"
            role="tabpanel"
            :aria-labelledby="`tab-${tab.id}`"
            :hidden="activeTab !== tab.id"
          >
            <OverviewView v-if="tab.id === 'dashboard' && activeTab === 'dashboard'" @navigate="selectTab" />
            <AssistantView v-if="tab.id === 'assistant' && activeTab === 'assistant'" />
            <ExpensesView v-if="tab.id === 'transactions' && activeTab === 'transactions'" />
            <BudgetsView v-if="tab.id === 'budgets' && activeTab === 'budgets'" @navigate="selectTab" />
            <InsightsView v-if="tab.id === 'insights' && activeTab === 'insights'" @navigate="selectTab" />
            <GroceryView v-if="tab.id === 'grocery' && activeTab === 'grocery'" />
            <HouseholdView v-if="tab.id === 'household' && activeTab === 'household'" />
            <SettingsView v-if="tab.id === 'settings' && activeTab === 'settings'" />
          </div>
        </PullToRefresh>
      </main>

      <!-- Bottom tab bar - phones only; the sidebar drawer stays available
           behind the "More" item for the remaining sections -->
      <nav class="bottom-nav" :aria-label="$t('app.bottom_navigation')">
        <button
          v-for="tab in bottomNavTabs"
          :key="tab.id"
          type="button"
          class="bottom-nav-item"
          :class="{ active: activeTab === tab.id && !sidebarOpen }"
          :aria-current="activeTab === tab.id ? 'page' : undefined"
          @click="selectBottomNavTab(tab.id)"
        >
          <component :is="tab.icon" class="bottom-nav-icon" aria-hidden="true" />
          <!-- Dedicated short labels: the full names ("Transactions",
               "Transaksjoner") ellipsize in a 6-slot bar on a 390px screen. -->
          <span class="bottom-nav-label">{{ $t(`navigation.short.${tab.id}`) }}</span>
        </button>
        <button
          type="button"
          class="bottom-nav-item"
          :class="{ active: sidebarOpen || !isBottomNavTab(activeTab) }"
          :aria-expanded="sidebarOpen"
          aria-controls="main-navigation"
          @click="toggleSidebar"
        >
          <svg class="bottom-nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
          </svg>
          <span class="bottom-nav-label">{{ $t('navigation.more') }}</span>
        </button>
      </nav>

      <!-- Account Upgrade Banner for Anonymous Users -->
      <AccountUpgrade ref="accountUpgradeRef" @upgraded="handleAccountUpgrade" />

    </template>

    <!-- Sign-out confirmation. Guests get the destructive framing and the
         linking flow as the way out — see UserProfile's copy of this. -->
    <ConfirmDialog
      v-model="showSignOutConfirm"
      :title="isAnonymous ? $t('confirm.sign_out_guest_title') : $t('confirm.sign_out_title')"
      :message="isAnonymous ? $t('confirm.sign_out_guest_message') : $t('confirm.sign_out_message')"
      :confirm-text="isAnonymous ? $t('confirm.sign_out_guest_confirm') : $t('confirm.sign_out_title')"
      :cancel-text="$t('common.cancel')"
      :alt-text="isAnonymous ? $t('confirm.sign_out_guest_keep') : undefined"
      :variant="isAnonymous ? 'danger' : 'warning'"
      @confirm="executeSignOut"
      @alt="accountUpgradeRef?.openModal?.()"
    />

    <!-- Authentication Modal -->
    <AuthModal
      :show-modal="showAuthModal"
      :initial-mode="authModalMode"
      @close="showAuthModal = false"
      @success="handleAuthSuccess"
    />

    <!-- Magic Link Handler -->
    <MagicLinkHandler
      @navigate="selectTab"
      @show-auth="showAuthModal = true; authModalMode = 'login'"
    />

    <!-- Set-new-password modal (arrival via password-reset email link) -->
    <PasswordRecoveryModal />

    <!-- User Profile Modal. UserProfile renders its own title + close button,
         so AppModal's default header is suppressed (no title, no close). -->
    <AppModal
      v-model="showUserProfile"
      size="lg"
      :aria-label="$t('app.profile_settings')"
      :show-close="false"
    >
      <UserProfile
        @close="showUserProfile = false"
        @upgrade="handleUpgradeFromProfile"
        @navigate="selectTab"
      />
    </AppModal>

    <!-- Onboarding Flow for New Users -->
    <OnboardingView
      v-if="showOnboarding"
      @complete="handleOnboardingComplete"
    />

    <!-- Post-purchase bank setup: the "migration" step offered once a user
         subscribes (bank sync is a paid feature). -->
    <BankSetupView
      v-if="showBankSetup"
      @close="handleBankSetupClose"
    />

    <!-- Loading Overlay during auth initialization -->
    <div
      v-if="isAuthLoading"
      class="fixed inset-0 flex flex-col items-center justify-center z-critical"
      style="background: var(--bg-primary);"
      role="alert"
      aria-live="assertive"
      aria-busy="true"
    >
      <div class="spinner mb-4" aria-hidden="true"></div>
      <p style="color: var(--text-tertiary); font-size: 0.8125rem; text-transform: uppercase; letter-spacing: 0.1em;">{{ $t('common.loading') }}</p>
    </div>

    <!-- Biometric Lock Screen -->
    <BiometricLockScreen />

    <!-- Global Search (Command Palette) -->
    <GlobalSearch
      :open="globalSearchOpen"
      :visible-tabs="searchableTabIds"
      @update:open="handleSearchOpenChange"
      @navigate="handleSearchNavigate"
      @action="handleSearchAction"
    />

    <!-- Offline / sync status pill -->
    <OfflineBanner />

    <!-- Hard data-load failure pill with retry (offline is OfflineBanner's job) -->
    <DataLoadErrorBanner />

    <!-- Global toast notifications -->
    <ToastContainer />
  </div>
</template>

<script setup lang="ts">
import { App as CapacitorApp } from "@capacitor/app"
import { Capacitor } from "@capacitor/core"
import { computed, h, onMounted, onUnmounted, ref, type VNode, watch } from "vue"
import { useI18n } from "vue-i18n"
import "./styles/mobile.css"
// VALUE import, not `import type`: a type-only import erases at runtime, so
// <AccountUpgrade> above failed to resolve and the guest account-linking UI
// never rendered at all. accountUpgradeRef is typed structurally so Biome's
// useImportType fix can't quietly turn this back into a type-only import.
import AccountUpgrade from "./components/AccountUpgrade.vue"
import AuthModal from "./components/AuthModal.vue"
import BiometricLockScreen from "./components/BiometricLockScreen.vue"
import BudgetContextSwitcher from "./components/BudgetContextSwitcher.vue"
import ConfirmDialog from "./components/ConfirmDialog.vue"
import DataLoadErrorBanner from "./components/DataLoadErrorBanner.vue"
import LanguageSwitcher from "./components/LanguageSwitcher.vue"
import MagicLinkHandler from "./components/MagicLinkHandler.vue"
import OfflineBanner from "./components/OfflineBanner.vue"
import PasswordRecoveryModal from "./components/PasswordRecoveryModal.vue"
import PullToRefresh from "./components/PullToRefresh.vue"
import ThemeToggle from "./components/ThemeToggle.vue"
import ToastContainer from "./components/ToastContainer.vue"
import UserProfile from "./components/UserProfile.vue"
import { AppAvatar, AppButton, AppModal } from "./components/ui"
import { useToast } from "./composables/useToast"
import { switchLanguage } from "./i18n"
import { pathForTab, tabForPath } from "./router/tabRoutes"
import { refetchAllSubscriptions } from "./services/apiClient"
import { bankService, triggerBankRefresh, triggerBankSelect } from "./services/bankService"
import { hapticLight } from "./services/hapticsService"
import { hideLaunchSplash, initNativeUx } from "./services/nativeUxService"
import { startRefetchPolling } from "./services/refetchPolling"
import {
  currentUser,
  initializeAuth,
  isAnonymous,
  isGuest,
  logout,
  signInAnonymously,
  subscribeToAuthChanges,
} from "./stores/authStore"
import {
  handleAppResume,
  initBiometricStore,
  recordAuthentication,
  shouldShowLockScreen,
} from "./stores/biometricStore"
import { firestoreState, isFirestoreInitialized, updateUserPreferences } from "./stores/firestoreStore"
import { hasPermission, isPersonalContext } from "./stores/householdStore"
import { initNotificationStore, refreshAllNotifications } from "./stores/notificationStore"
import {
  beginOrResumeOnboarding,
  endBankSetup,
  failBankSetup,
  isBankSetupActive,
  isResumingBankSetup,
  onboardingState,
  resumeBankSetupIfPending,
  setBankPhase,
  startBankSetup,
} from "./stores/onboardingStore"
import { canUseBank, refreshSubscriptionStatus } from "./stores/subscriptionStore"
import {
  closeGlobalSearch,
  dismissTopModal,
  dispatchAction,
  globalSearchOpen,
  openGlobalSearch as openGlobalSearchModal,
  pendingUpgradeOpen,
  requestSearchOpen,
} from "./stores/uiStore"
import { localizeAuthError } from "./utils/authErrors"
import { defineLazyView } from "./utils/chunkReload"
import { bottomNavTabIds, isTabHidden } from "./utils/focusMode"
import { logger } from "./utils/logger"
// Overview is the landing tab — load it eagerly so first paint of the
// authenticated UI is instant. Every other tab is split into its own chunk
// so navigating to e.g. Budgets doesn't ship Settings/Household/Grocery
// code in the initial bundle.
import OverviewView from "./views/OverviewView.vue"

const AssistantView = defineLazyView(() => import("./views/AssistantView.vue"))
const ExpensesView = defineLazyView(() => import("./views/ExpensesView.vue"))
const BudgetsView = defineLazyView(() => import("./views/BudgetsView.vue"))
const InsightsView = defineLazyView(() => import("./views/InsightsView.vue"))
const GroceryView = defineLazyView(() => import("./views/GroceryView.vue"))
const HouseholdView = defineLazyView(() => import("./views/HouseholdView.vue"))
const SettingsView = defineLazyView(() => import("./views/SettingsView.vue"))
const OnboardingView = defineLazyView(() => import("./views/OnboardingView.vue"))
const BankSetupView = defineLazyView(() => import("./views/BankSetupView.vue"))
const GlobalSearch = defineLazyView(() => import("./components/GlobalSearch.vue"))

const { locale, t } = useI18n()
const toast = useToast()

// URL routing is web-only. Native (Capacitor) loads from a file/asset URL with
// no address bar, and rewriting the path there can break asset resolution on a
// reload, so we leave the in-memory tab navigation untouched on native.
const routingEnabled = !Capacitor.isNativePlatform()

// Derive the initial tab from the URL path so deep links / refreshes / the bank
// redirect (which returns to /settings) land on the right screen.
const initialTab = routingEnabled ? tabForPath(window.location.pathname) : null
const activeTab = ref(initialTab && !isTabHidden(initialTab) ? initialTab : "dashboard")
const sidebarOpen = ref(false)
const showAuthModal = ref(false)
const authModalMode = ref<"login" | "register">("login")
// Mirrors AuthModal's flag: sign-up UI is opt-in via VITE_ALLOW_REGISTRATION.
const REGISTRATION_ENABLED = import.meta.env.VITE_ALLOW_REGISTRATION === "true"
const guestLoading = ref(false)
const guestError = ref<string | null>(null)
const showUserProfile = ref(false)
const isAuthLoading = ref(true)
const showOnboarding = ref(false)
const announcement = ref("")

// Watch for when we should show onboarding (after firestore is initialized for authenticated users)
const shouldCheckOnboarding = computed(() => {
  return !isAuthLoading.value && !isGuest.value && isFirestoreInitialized.value
})

watch(
  shouldCheckOnboarding,
  (shouldCheck) => {
    if (!shouldCheck) return
    // One call decides both whether to show onboarding and where to resume it —
    // returning from a bank redirect has to reopen at the bank step rather than
    // restart at welcome. See beginOrResumeOnboarding.
    showOnboarding.value = beginOrResumeOnboarding(currentUser.value?.uid ?? null)
  },
  { immediate: true },
)

// True while the standalone bank-setup overlay owns the bank flow. The redirect
// handlers below defer to it: it hosts its own account picker, so their usual
// "jump to Settings and raise the picker flag there" behaviour would fight it
// for the screen.
//
// Deliberately not keyed on showBankSetup — that's a computed off store state
// the redirect handlers themselves establish (via resumeBankSetupIfPending at
// mount). See isResumingBankSetup, which answers synchronously off the
// breadcrumb, before any of that has run.
const bankFlowOwnedBySetup = () => isResumingBankSetup(currentUser.value?.uid ?? null)

function handleOnboardingComplete(target?: string) {
  showOnboarding.value = false
  if (target) selectTab(target)
}

// The post-purchase "connect your bank" overlay is on screen whenever the flow
// is active in the store — set either by the offer below or by a redirect
// resume. Never alongside onboarding (post-purchase users have finished it).
const showBankSetup = computed(() => isBankSetupActive.value && !showOnboarding.value)

// Offer the one-off bank-setup step once a user reaches a paid tier. The Vipps
// checkout navigates the whole SPA away, so we can't catch the free→paid flip
// live — instead we detect the entitled-but-not-yet-prompted state on load.
// Guarded so it fires at most once per session and never ambushes a subscriber
// who already linked a bank in Settings.
const bankSetupOfferChecked = ref(false)
watch(
  [shouldCheckOnboarding, canUseBank],
  async () => {
    if (bankSetupOfferChecked.value) return
    if (!shouldCheckOnboarding.value || !canUseBank.value) return
    // Don't collide with onboarding or a bank link already resuming from a redirect.
    if (showOnboarding.value || isBankSetupActive.value) return
    if (isResumingBankSetup(currentUser.value?.uid ?? null)) return
    if (firestoreState.preferences.value?.bankSetupPrompted) {
      bankSetupOfferChecked.value = true
      return
    }
    bankSetupOfferChecked.value = true
    try {
      // Already has a bank? They don't need the nudge — record it and move on.
      const connections = await bankService.listConnections()
      if (connections.length > 0) {
        void updateUserPreferences({ bankSetupPrompted: true })
        return
      }
    } catch {
      // Couldn't check (offline / transient) — let a later load try again.
      bankSetupOfferChecked.value = false
      return
    }
    startBankSetup()
  },
  { immediate: true },
)

// Closing the overlay (finished, backed out, or "later") records that we've
// prompted, so the offer above never reopens it.
function handleBankSetupClose() {
  endBankSetup()
  void updateUserPreferences({ bankSetupPrompted: true })
}

// Auth state refs for reactivity
const authUnsubscribe = ref<(() => void) | null>(null)

// Type for tabs
interface TabItem {
  id: string
  icon: () => VNode
  requiresPermission?: Parameters<typeof hasPermission>[0]
}

const iconSvg = (paths: string) => () =>
  h(
    "svg",
    { class: "icon", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" },
    paths
      .split("|")
      .map((path) =>
        h("path", { "stroke-linecap": "round", "stroke-linejoin": "round", "stroke-width": "2", d: path }),
      ),
  )

// All available tabs (order = sidebar order)
const allTabs: TabItem[] = [
  {
    id: "dashboard",
    icon: iconSvg(
      "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2z|M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z",
    ),
  },
  {
    id: "assistant",
    icon: iconSvg(
      "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-4 4v-4z",
    ),
  },
  {
    // Two-way arrows: money in / money out. (The old shopping-bag icon read
    // as "shopping" and clashed with the grocery cart below. Recurring income
    // management lives under Budgets → Income now.)
    id: "transactions",
    icon: iconSvg("M8 7h12m0 0l-4-4m4 4l-4 4|M16 17H4m0 0l4 4m-4-4l4-4"),
  },
  {
    id: "budgets",
    icon: iconSvg(
      "M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z",
    ),
  },
  {
    id: "insights",
    icon: iconSvg(
      "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
    ),
    requiresPermission: "canViewAIInsights",
  },
  {
    id: "grocery",
    icon: iconSvg(
      "M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z",
    ),
  },
  {
    id: "household",
    icon: iconSvg(
      "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
    ),
  },
  {
    id: "settings",
    icon: iconSvg(
      "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z|M15 12a3 3 0 11-6 0 3 3 0 016 0z",
    ),
  },
]

// Filter tabs based on user permissions
const tabs = computed(() => {
  return allTabs.filter((tab) => {
    if (isTabHidden(tab.id)) return false
    if (!tab.requiresPermission) return true
    return hasPermission(tab.requiresPermission)
  })
})

// The highest-traffic sections get a slot in the phone bottom tab bar;
// everything else lives behind the "More" item (the sidebar drawer).
// Insights earns a slot so the AI-analysis headline feature isn't buried in
// the drawer; it drops out automatically when the user lacks the permission.
// Focus mode swaps Insights + Assistant for Household (utils/focusMode.ts).
const BOTTOM_NAV_TAB_IDS = bottomNavTabIds()
const bottomNavTabs = computed(() =>
  BOTTOM_NAV_TAB_IDS.map((id) => tabs.value.find((tab) => tab.id === id)).filter(
    (tab): tab is TabItem => tab !== undefined,
  ),
)

function isBottomNavTab(tabId: string): boolean {
  return BOTTOM_NAV_TAB_IDS.includes(tabId)
}

function selectBottomNavTab(tabId: string) {
  hapticLight()
  sidebarOpen.value = false
  selectTab(tabId)
}

// Apply a tab without touching the URL — used on initial load and when the URL
// already reflects the target (e.g. browser back/forward via popstate).
function applyTab(requested: string) {
  // A hidden tab (focus mode) can still be reached by an old link or a stray
  // navigate event; land on the dashboard rather than render it.
  const tabId = isTabHidden(requested) ? "dashboard" : requested
  const changed = activeTab.value !== tabId
  activeTab.value = tabId
  sidebarOpen.value = false
  // The document is the scroller — the views swap inside it, so without this
  // the new tab inherits the old one's offset and opens halfway down the page.
  // (Scrolled through Transactions, tapped Budgets, landed mid-category-list.)
  if (changed) window.scrollTo({ top: 0 })
  // Announce tab change for screen readers, in the user's language
  announcement.value = t("a11y.navigated_to", { section: t(`navigation.${tabId}`) })
}

function selectTab(requested: string) {
  const tabId = isTabHidden(requested) ? "dashboard" : requested
  applyTab(tabId)
  // Keep the browser URL in sync so each tab has its own real, shareable path.
  if (routingEnabled) {
    const path = pathForTab(tabId)
    if (path !== window.location.pathname) {
      try {
        window.history.pushState({ tab: tabId }, "", path)
      } catch {
        // History API can be unavailable in some embedded contexts — non-fatal.
      }
    }
  }
}

// Sync the active tab when the user navigates with the browser back/forward
// buttons. Reserved/unknown paths fall back to the dashboard.
function handlePopState() {
  if (!routingEnabled) return
  applyTab(tabForPath(window.location.pathname) ?? "dashboard")
}

// Pull-to-refresh: force every live API subscription to refetch, then refresh
// the notification badges that derive from that data. Realtime normally keeps
// things current, but this gives users a manual "I want fresh data now" lever.
// The subscription tier is refreshed too — e.g. right after returning from a
// Vipps upgrade.
async function handlePullToRefresh() {
  const { anyFailed } = await refetchAllSubscriptions()
  await Promise.all([refreshAllNotifications().catch(() => {}), refreshSubscriptionStatus().catch(() => {})])
  // Refetch failures are swallowed per-resource — tell the user the pull
  // didn't fully land instead of silently showing stale data.
  if (anyFailed) toast.warning(t("common.refresh_failed"))
}

function openGlobalSearch() {
  openGlobalSearchModal()
}

function handleSearchOpenChange(value: boolean) {
  if (value) openGlobalSearchModal()
  else closeGlobalSearch()
}

// Tab IDs the search palette is allowed to navigate to (after permission filtering)
const searchableTabIds = computed(() => tabs.value.map((t) => t.id))

function handleSearchNavigate(tab: string, focus?: { kind: string; id?: string } | null) {
  selectTab(tab)
  closeGlobalSearch()
  if (focus?.id) {
    // Surface the focus target to the destination view via the action bus
    dispatchAction(`focus-${focus.kind}`, focus)
  }
}

function handleSearchAction(action: string, focus?: { kind: string; id?: string } | null) {
  closeGlobalSearch()
  switch (action) {
    case "open-assistant":
      selectTab("assistant")
      break
    case "add-expense":
      selectTab("transactions")
      dispatchAction("add-expense", focus)
      break
    case "add-income":
      // Recurring income is managed on the Budgets view's Income tab
      selectTab("budgets")
      dispatchAction("add-income", focus)
      break
    case "add-account":
      // The add-account form lives in Settings with the accounts manager
      selectTab("settings")
      dispatchAction("add-account", focus)
      break
    case "add-category":
      selectTab("budgets")
      dispatchAction("add-category", focus)
      break
    case "add-savings-goal":
      selectTab("budgets")
      dispatchAction("add-savings-goal", focus)
      break
    case "add-investment":
      selectTab("budgets")
      dispatchAction("add-investment", focus)
      break
    case "filter-transactions":
      selectTab("transactions")
      requestSearchOpen()
      break
    case "export-data":
      selectTab("settings")
      dispatchAction("export-data", focus)
      break
    case "import-data":
      // The JSON import modal lives on the Overview (dashboard) view
      selectTab("dashboard")
      dispatchAction("import-data", focus)
      break
    case "weekly-review":
      // The review sheet is hosted by the dashboard, like the import modal
      selectTab("dashboard")
      dispatchAction("weekly-review", focus)
      break
    case "toggle-theme":
      toggleAppTheme()
      break
    case "switch-language":
      switchAppLanguage()
      break
    case "open-profile":
      showUserProfile.value = true
      break
    case "refresh-ai":
      selectTab("insights")
      dispatchAction("refresh-ai", focus)
      break
    case "sign-out":
      void handleSignOut()
      break
    default:
      break
  }
}

// Mirror the active budget context onto <html data-pot> so the whole app
// re-tints when you switch pots: style.css derives --pot (and therefore
// --accent) from it. Kept on the root element rather than passed down as a
// prop so teleported overlays — modals, sheets, the context dropdown — inherit
// the same pot colour as the content behind them.
// Signed out there is no "your money" yet, and the context store defaults to
// personal — so a stranger would meet the brass pot on the welcome screen
// before they have one. isGuest is the same condition that screen renders on,
// so the two can never disagree.
watch(
  [isPersonalContext, isGuest],
  ([personal, guest]) => {
    document.documentElement.setAttribute("data-pot", !guest && personal ? "personal" : "shared")
  },
  { immediate: true },
)

// Toggle the app theme (light/dark) — kept in sync with the same storage
// key ThemeToggle.vue uses so they stay consistent.
function toggleAppTheme() {
  const current = document.documentElement.getAttribute("data-theme") || "light"
  const next = current === "dark" ? "light" : "dark"
  document.documentElement.setAttribute("data-theme", next)
  try {
    localStorage.setItem("theme", next)
  } catch {
    // localStorage may be unavailable in private mode
  }
}

// Toggle between English and Norwegian — the only two supported locales.
// Persists via the shared i18n helper so it uses the same storage key the
// app reads back on startup ("language" — writing "locale" here used to
// silently lose the choice on reload).
function switchAppLanguage() {
  const next = locale.value === "no" ? "en" : "no"
  try {
    switchLanguage(next)
  } catch {
    // localStorage may be unavailable — still switch for this session
    locale.value = next
  }
}

// The command palette's "sign out" used to log straight out with no prompt.
// For a named account that is merely abrupt; for a guest it silently destroys
// the account and every row behind it, because the anonymous uid is the only
// credential and it lives in local storage. Both now confirm first.
const showSignOutConfirm = ref(false)

function handleSignOut() {
  showSignOutConfirm.value = true
}

async function executeSignOut() {
  try {
    await logout()
  } catch {
    // Surface via toast container — errors are non-fatal here
  }
}

// Global keyboard shortcut: Cmd/Ctrl+K opens the search palette
function handleGlobalKeydown(e: KeyboardEvent) {
  // Ignore if user is mid-IME composition
  if (e.isComposing) return
  const isMeta = e.metaKey || e.ctrlKey
  if (isMeta && (e.key === "k" || e.key === "K")) {
    e.preventDefault()
    openGlobalSearchModal()
  } else if (isMeta && e.key === "/") {
    e.preventDefault()
    openGlobalSearchModal()
  }
}

// Accessibility: Skip to main content
function skipToMain() {
  const main = document.getElementById("main-content")
  if (main) {
    main.setAttribute("tabindex", "-1")
    main.focus()
    main.removeAttribute("tabindex")
  }
}

// Accessibility: Toggle sidebar with announcement
function toggleSidebar() {
  sidebarOpen.value = !sidebarOpen.value
  announcement.value = sidebarOpen.value ? t("a11y.menu_opened") : t("a11y.menu_closed")
}

// Accessibility: Keyboard navigation for tabs
function handleTabKeydown(event: KeyboardEvent, currentTabId: string) {
  const tabIds = tabs.value.map((t) => t.id)
  const currentIndex = tabIds.indexOf(currentTabId)

  let newIndex = currentIndex
  if (event.key === "ArrowDown" || event.key === "ArrowRight") {
    event.preventDefault()
    newIndex = (currentIndex + 1) % tabIds.length
  } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
    event.preventDefault()
    newIndex = (currentIndex - 1 + tabIds.length) % tabIds.length
  } else if (event.key === "Home") {
    event.preventDefault()
    newIndex = 0
  } else if (event.key === "End") {
    event.preventDefault()
    newIndex = tabIds.length - 1
  }

  if (newIndex !== currentIndex) {
    selectTab(tabIds[newIndex])
    // Focus the new tab button
    const newTabButton = document.getElementById(`tab-${tabIds[newIndex]}`)
    newTabButton?.focus()
  }
}

function handleAuthSuccess() {
  showAuthModal.value = false
  // Record authentication for biometric unlock tracking
  recordAuthentication()
}

// "Continue as guest" — anonymous Supabase session; upgradeable to a full
// account later via AccountUpgrade. Requires anonymous sign-ins to be
// enabled in Supabase Auth.
async function handleGuestSignIn() {
  guestLoading.value = true
  guestError.value = null
  try {
    await signInAnonymously()
    recordAuthentication()
  } catch (err: unknown) {
    // Localize like every other auth surface — the raw value here is a Firebase
    // code ("auth/admin-restricted-operation"), which is both untranslated and
    // meaningless to a user, and the old English fallback was no better.
    guestError.value = localizeAuthError(err, t)
  } finally {
    guestLoading.value = false
  }
}

function handleAccountUpgrade() {
  // Account was successfully upgraded from anonymous to authenticated
}

// AccountUpgrade's linking flow (linkAnonymousWith*) keeps the guest's data
// under the same uid. AuthModal must NOT be used here — its register/login
// creates/signs into a different account and orphans everything the guest
// entered.
const accountUpgradeRef = ref<{ openModal?: () => void } | null>(null)

function handleUpgradeFromProfile() {
  showUserProfile.value = false
  accountUpgradeRef.value?.openModal?.()
}

// Feature gates elsewhere (AI assistant/insights) ask for the same modal via
// the uiStore, since they can't reach the template ref.
watch(pendingUpgradeOpen, (open) => {
  if (!open) return
  pendingUpgradeOpen.value = false
  accountUpgradeRef.value?.openModal?.()
})

// App resume listener cleanup
const appResumeListener = ref<any>(null)
const stopRefetchPolling = ref<(() => void) | null>(null)
const backButtonListener = ref<any>(null)

// Android hardware back: dismiss whatever is topmost (registered overlay —
// edit modal, bottom/action sheet, confirm dialog —, search palette, profile
// dialog, auth modal, nav drawer), then step back to the dashboard, and only
// minimize the app once we're already on the dashboard with nothing open.
// Without this listener Capacitor exits the app on the first press.
function handleHardwareBack() {
  if (shouldShowLockScreen.value) {
    // Locked: the app behind the gate must stay untouched. Back means "leave",
    // not "navigate under the lock screen".
    void CapacitorApp.minimizeApp()
  } else if (dismissTopModal()) {
    // An open overlay consumed the press
  } else if (globalSearchOpen.value) {
    closeGlobalSearch()
  } else if (showUserProfile.value) {
    showUserProfile.value = false
  } else if (showAuthModal.value) {
    showAuthModal.value = false
  } else if (sidebarOpen.value) {
    sidebarOpen.value = false
  } else if (activeTab.value !== "dashboard") {
    selectTab("dashboard")
  } else {
    void CapacitorApp.minimizeApp()
  }
}

// Show the outcome of a server-side Open Banking link. The bank redirects to a
// public Edge Function that exchanges the code and links the connection itself
// (robust against browser cache / CSP / PWA storage quirks), then bounces back
// here with ?bank=linked|error|notfound. We just surface a toast, refresh the
// list, and strip the param — the connection's real status comes from the API.
function handleBankRedirectResult() {
  const params = new URLSearchParams(window.location.search)
  const bank = params.get("bank")
  if (!bank) return
  const cid = params.get("cid")

  if (bankFlowOwnedBySetup()) {
    // The bank-setup overlay started this link and renders the outcome on its
    // own screen, so all this has to do is move it to the right phase. The
    // redirect is the ONLY thing that knows the link came back — the step
    // persisted "connecting" before handing off to the bank and has been rebuilt
    // from that breadcrumb since, so without this it would sit on the connecting
    // spinner forever. (resumeBankSetupIfPending, called at mount, reopens the
    // overlay off the same breadcrumb.)
    //
    // Everything in the else branch would actively fight it: selectTab moves the
    // tab under the overlay, the toast narrates a step the user can already see,
    // and triggerBankSelect arms a picker in Settings that the overlay is about
    // to show itself. That flag is sticky with no owner (see bankService), so
    // raising it here would ambush the user with a stray picker the next time
    // they opened Settings — possibly days later.
    const uid = currentUser.value?.uid ?? null
    const target = cid ?? onboardingState.bankConnectionId.value
    if (bank === "select") setBankPhase("selecting", target, uid)
    else if (bank === "linked") setBankPhase("deriving", target, uid)
    else failBankSetup()
  } else {
    selectTab("settings")
    if (bank === "select") {
      // Accounts are ready to choose — open the picker (BankConnections listens).
      triggerBankSelect()
    } else if (bank === "linked") {
      toast.success(t("bank.link_success"))
      triggerBankRefresh()
    } else {
      toast.error(t("bank.finalize_failed"))
      triggerBankRefresh()
    }
  }

  params.delete("bank")
  params.delete("cid")
  const qs = params.toString()
  window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""))
}

// Complete an Open Banking callback that landed on the app itself. Enable Banking
// often redirects to /settings?code=&state= (not our server callback); the
// Supabase pkce client would eat that code, so utils/bankCallbackCapture lifts it
// into sessionStorage before Supabase loads. Here we post it to the server to
// exchange it and move the connection to "selecting", then open the picker.
// Needs an authenticated session, which may not be ready at first mount — kept
// stashed until then, and retried by the currentUser watch below.
let bankExchangeInFlight = false
async function handleBankCallbackExchange() {
  const raw = sessionStorage.getItem("bank_callback")
  if (!raw) return
  if (!currentUser.value) return
  if (bankExchangeInFlight) return
  bankExchangeInFlight = true
  try {
    let payload: { code?: string; state?: string }
    try {
      payload = JSON.parse(raw) as { code?: string; state?: string }
    } catch {
      payload = {}
    }
    if (!payload.code || !payload.state) {
      sessionStorage.removeItem("bank_callback")
      return
    }
    // Same deference as handleBankRedirectResult: when the bank-setup overlay
    // owns the bank flow it drives its own screen, and both handlers can run on
    // one load (?bank= from the server callback and a lifted ?code= can arrive
    // together), so neither may assume it's the only one.
    const owned = bankFlowOwnedBySetup()
    if (!owned) selectTab("settings")
    const result = await bankService.exchangeCallback(payload.code, payload.state)
    // Only clear the stash once the server has actually consumed the code.
    // Dropping it before the request (as this used to do) meant any transient
    // failure — offline blip, expired token, flaky mobile network — lost the
    // single-use code for good and left the connection on "Pending" forever.
    sessionStorage.removeItem("bank_callback")
    if (owned) {
      // The bank-setup overlay is watching its own connection; just make sure
      // it's pointed at the right one and let its own step render the outcome.
      setBankPhase(
        result.status === "selecting" ? "selecting" : result.status === "linked" ? "deriving" : "selecting",
        result.connectionId,
        currentUser.value?.uid ?? null,
      )
    } else if (result.status === "selecting") {
      triggerBankSelect()
    } else if (result.status === "linked") {
      toast.success(t("bank.link_success"))
      triggerBankRefresh()
    } else {
      toast.error(t("bank.finalize_failed"))
      triggerBankRefresh()
    }
  } catch {
    // Request never got a response — keep the stash so the currentUser watch,
    // the 'online' listener, or the next app start in this tab can retry. The
    // server-side exchange is idempotent, so a duplicate post is harmless.
    toast.error(t("bank.finalize_failed"))
  } finally {
    bankExchangeInFlight = false
  }
}

// Retry a kept-back exchange when connectivity returns (the redirect from the
// bank often lands while the network is still flapping on mobile).
function retryBankExchangeOnline() {
  void handleBankCallbackExchange()
}

// Retry the exchange as soon as the auth session resolves (currentUser is often
// null for a beat on a cold load after returning from the bank).
watch(currentUser, (user) => {
  if (user) void handleBankCallbackExchange()
})

// Initialize auth on mount.
//
// Structured so a failure in any one step can't cost the app the rest of its
// wiring. This used to be a single try/catch around everything, which meant a
// transient error early on (a rejected store init, a plugin call throwing on an
// odd device) skipped the platform listeners below it — leaving Android with no
// hardware-back handler, so the very first back press quit the app.
onMounted(async () => {
  // 1. Platform wiring first: it depends on nothing and everything else can
  //    fail without it. Registered before the first await so a back press
  //    during a slow startup is already handled.
  installPlatformListeners()

  // 2. Auth, then the per-user stores. initializeAuth always settles (see
  //    authStore), so the loading gate below always lifts.
  try {
    await initializeAuth()

    // If user already authenticated (cached session), initialize Firestore
    // before clearing loading state — prevents empty data on deploy
    if (currentUser.value) {
      const { initializeFirestore } = await import("./stores/firestoreStore")
      await initializeFirestore(currentUser.value)
      // Load the user's subscription tier the same way, so paid features
      // aren't gated on the free defaults while the auth listener catches up.
      const { initializeSubscription } = await import("./stores/subscriptionStore")
      await initializeSubscription(currentUser.value.uid)
    }

    // Subscribe to future auth state changes (login/logout/switch)
    authUnsubscribe.value = subscribeToAuthChanges()

    // Must finish INSIDE the loading gate: it decides whether the biometric
    // lock screen goes up, and shouldShowLockScreen is false until it has read
    // the stored preference. Revealing the app first would flash the signed-in
    // user's finances for a frame before the lock covered them.
    await initBiometricStore().catch((error) => logger.warn("Biometric init failed", { error }))
  } catch (error) {
    logger.error("Auth startup failed; continuing to render the app", error)
  } finally {
    // Whatever happened, stop covering the UI with the loading overlay and let
    // the splash go — the user gets either the app, the lock screen, or the
    // signed-out screen, never an indefinite spinner.
    isAuthLoading.value = false
    void hideLaunchSplash()
  }

  // 3. Everything below is best-effort and independently guarded.
  try {
    // Reopen the bank-setup overlay if a link is mid-redirect (the breadcrumb
    // outlives the page navigation the connect flow triggers). Must run before
    // the handlers below, which move its phase on the way back.
    resumeBankSetupIfPending(currentUser.value?.uid ?? null)

    // Surface the result of a server-side bank link (?bank=…), and complete a
    // callback that landed on the app itself (lifted into sessionStorage).
    handleBankRedirectResult()
    await handleBankCallbackExchange()
  } catch (error) {
    logger.warn("Bank callback handling failed on startup", { error })
  }

  await initNotificationStore().catch((error) => logger.warn("Notification init failed", { error }))

  // Schedule bill reminders after a short delay to allow data to load
  setTimeout(() => {
    void refreshAllNotifications().catch((error) => logger.warn("Notification refresh failed", { error }))
  }, 3000)
})

// Listeners the app needs regardless of how startup goes. Each is guarded on
// its own so one unavailable API can't take the others down with it.
function installPlatformListeners() {
  if (Capacitor.isNativePlatform()) {
    // Set up app resume listener for biometric lock
    CapacitorApp.addListener("resume", () => {
      handleAppResume()
    })
      .then((handle) => {
        appResumeListener.value = handle
      })
      .catch((error) => logger.warn("Failed to register resume listener", { error }))

    CapacitorApp.addListener("backButton", handleHardwareBack)
      .then((handle) => {
        backButtonListener.value = handle
      })
      .catch((error) => logger.warn("Failed to register back-button listener", { error }))
  }

  // Orientation lock (phones) + soft-keyboard handling. No-op on web.
  void initNativeUx().catch((error) => logger.warn("Native UX init failed", { error }))

  // Register global Cmd/Ctrl+K shortcut for the search palette
  window.addEventListener("keydown", handleGlobalKeydown)

  window.addEventListener("online", retryBankExchangeOnline)

  // Freshness backstop: slow poll + refetch-on-foreground. Becomes the ONLY
  // live-update mechanism once the database leaves Supabase (Realtime dies
  // with it) — see services/refetchPolling.ts.
  try {
    stopRefetchPolling.value = startRefetchPolling()
  } catch (error) {
    logger.warn("Failed to start refetch polling", { error })
  }

  // Keep the active tab in sync with browser back/forward navigation
  if (routingEnabled) {
    window.addEventListener("popstate", handlePopState)
  }
}

// Cleanup on unmount
onUnmounted(() => {
  if (authUnsubscribe.value) {
    authUnsubscribe.value()
  }
  stopRefetchPolling.value?.()
  if (appResumeListener.value) {
    appResumeListener.value.remove()
  }
  if (backButtonListener.value) {
    backButtonListener.value.remove()
  }
  window.removeEventListener("keydown", handleGlobalKeydown)
  window.removeEventListener("online", retryBankExchangeOnline)
  if (routingEnabled) {
    window.removeEventListener("popstate", handlePopState)
  }
})
</script>